import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, Grid,
  InputLabel, MenuItem, Select, Stack, Switch, Tab, Tabs, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Paper
} from '@mui/material';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const FREQUENCIES = [
  { id: 'minutely', label: 'Minutely' },
  { id: 'hourly', label: 'Hourly' },
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly' },
];

const PROVIDER_OPTIONS = [
  { id: 'gmail', label: 'Gmail' },
  { id: 'outlook', label: 'Outlook / Office365' },
  { id: 'yahoo', label: 'Yahoo' },
  { id: 'hotmail', label: 'Hotmail / Live' },
  { id: 'other', label: 'Other IMAP' },
];

/**
 * Admin → Settings → Email Extractor
 * 0 Job · 1 Email Providers · 2 Promote · exclusions on Job tab
 */
export default function EmailExtractorSettingsPanel() {
  const [subTab, setSubTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    frequency: 'hourly',
    startDate: '',
    endDate: '',
    providers: ['gmail', 'outlook', 'yahoo'],
    maxResults: 100,
    userIds: [],
    accountIds: [],
    exclusionPatterns: ['*noreply*', '*no-reply*', '*donotreply*', '*mailer-daemon*'],
  });
  const [exclusionText, setExclusionText] = useState('');
  const [viewRun, setViewRun] = useState(null);
  const [tags, setTags] = useState([]);
  const [promoteTag, setPromoteTag] = useState('');
  const [promoteMode, setPromoteMode] = useState('contact');
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [promotePreview, setPromotePreview] = useState([]);
  const [providerAccounts, setProviderAccounts] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);

  const loadProviders = useCallback(async () => {
    setProvidersLoading(true);
    try {
      const r = await api.get('/email-extractor/providers/admin/accounts');
      setProviderAccounts(r.data?.data?.accounts || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load provider accounts');
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [st, tg] = await Promise.all([
        api.get('/email-extractor/job/status'),
        api.get('/email-extractor/tags'),
      ]);
      const data = st.data?.data || {};
      setStatus(data);
      const cfg = data.config || {};
      const patterns = Array.isArray(cfg.exclusionPatterns)
        ? cfg.exclusionPatterns
        : ['*noreply*', '*no-reply*', '*donotreply*', '*mailer-daemon*'];
      setForm({
        enabled: Boolean(cfg.enabled),
        frequency: cfg.frequency || 'hourly',
        startDate: cfg.startDate || '',
        endDate: cfg.endDate || '',
        providers: Array.isArray(cfg.providers) ? cfg.providers : ['gmail', 'outlook', 'yahoo'],
        maxResults: cfg.maxResults || 100,
        userIds: Array.isArray(cfg.userIds) ? cfg.userIds : [],
        accountIds: Array.isArray(cfg.accountIds) ? cfg.accountIds : [],
        exclusionPatterns: patterns,
      });
      setExclusionText(patterns.join('\n'));
      setTags(tg.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load Email Extractor settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (subTab === 1) loadProviders();
  }, [subTab, loadProviders]);

  const usersFromProviders = useMemo(() => {
    const map = new Map();
    for (const a of providerAccounts) {
      if (!a.user?.id) continue;
      if (!map.has(a.user.id)) {
        map.set(a.user.id, {
          id: a.user.id,
          label: `${a.user.first_name || ''} ${a.user.last_name || ''}`.trim() || a.user.email,
          email: a.user.email,
        });
      }
    }
    return [...map.values()];
  }, [providerAccounts]);

  const save = async () => {
    setSaving(true);
    try {
      const patterns = exclusionText
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      await api.put('/email-extractor/job/config', {
        ...form,
        exclusionPatterns: patterns,
      });
      toast.success('Email Extractor job settings saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    try {
      const patterns = exclusionText
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const r = await api.post('/email-extractor/job/run-now', {
        ...form,
        enabled: true,
        exclusionPatterns: patterns,
      });
      toast.success(r.data?.message || 'Job started and left enabled on schedule');
      setForm((f) => ({ ...f, enabled: true }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not start job');
    }
  };

  const openCurrentRun = async () => {
    const id = status?.currentRun?.id;
    if (!id) {
      toast.info('No active/current run to view — open a completed run from the log after refresh');
      return;
    }
    try {
      const r = await api.get(`/email-extractor/job/runs/${id}`);
      setViewRun(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load run details');
    }
  };

  const openLatestRun = async () => {
    try {
      const r = await api.get('/email-extractor/job/runs?page=1&limit=1');
      const row = r.data?.data?.[0];
      if (!row) {
        toast.info('No job runs yet');
        return;
      }
      setViewRun(row);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load runs');
    }
  };

  const toggleProvider = (id) => {
    setForm((f) => {
      const set = new Set(f.providers);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...f, providers: [...set] };
    });
  };

  const toggleAccountScope = (id) => {
    setForm((f) => {
      const set = new Set(f.accountIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...f, accountIds: [...set] };
    });
  };

  const toggleUserScope = (id) => {
    setForm((f) => {
      const set = new Set(f.userIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...f, userIds: [...set] };
    });
  };

  const setProviderEnabled = async (id, enabled) => {
    try {
      await api.patch(`/email-extractor/providers/admin/accounts/${id}`, {
        enabled,
        reason: enabled ? null : 'Disabled by administrator',
      });
      toast.success(enabled ? 'Provider enabled' : 'Provider disabled for user + job');
      await loadProviders();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Update failed');
    }
  };

  const loadPromotePreview = async () => {
    if (!promoteTag) {
      setPromotePreview([]);
      return;
    }
    const params = new URLSearchParams({
      page: 1,
      limit: 100,
      promotionStatus: 'none',
    });
    if (promoteTag.startsWith('Company-')) params.set('companyTag', promoteTag);
    if (promoteTag.startsWith('Contact-')) params.set('contactTag', promoteTag);
    try {
      const r = await api.get(`/email-extractor/emails?${params}`);
      setPromotePreview(r.data?.data || []);
    } catch (e) {
      toast.error('Failed to load emails for tag');
    }
  };

  useEffect(() => {
    if (subTab === 2) loadPromotePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab, promoteTag]);

  const runPromoteByTag = async () => {
    const ids = promotePreview.map((e) => e.id);
    if (!ids.length) {
      toast.info('No unpromoted emails match this tag');
      return;
    }
    setPromoteLoading(true);
    try {
      const r = await api.post('/email-extractor/emails/bulk/promote', {
        email_ids: ids,
        mode: promoteMode,
        contact_type: 'regular',
      });
      toast.success(r.data?.message || `Promoted ${r.data?.data?.promoted || 0}`);
      await loadPromotePreview();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Promote failed');
    } finally {
      setPromoteLoading(false);
    }
  };

  if (loading && !status) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const runStatus = status?.status || 'idle';
  const isRunning = runStatus === 'running';
  const statusLabel =
    isRunning
      ? 'Running now'
      : runStatus === 'scheduled'
        ? 'Active — waiting for next run'
        : runStatus === 'disabled'
          ? 'Disabled'
          : String(runStatus);

  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }} variant="scrollable">
        <Tab label="Email Extractor Job" />
        <Tab label="Email Providers" />
        <Tab label="Promote Emails" />
      </Tabs>

      {subTab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Run now</strong> starts an immediate extract and leaves the job <strong>enabled</strong> so the
            worker keeps running on the chosen frequency (e.g. every minute). Status <em>scheduled</em> means waiting
            for the next tick — not disabled.
          </Alert>

          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>How tags are decided:</strong> Company-Good = corporate domain + company name;
            Company-Need-Review = corporate but weak name; Company-No = free-mail / noreply patterns.
            Contact-Good = person name present; Contact-Need-Review = role mailbox (info@, sales@) or weak name;
            Contact-No = noreply / no contact signal. Domain exclusions and patterns below are applied during extract.
          </Alert>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Status:
            </Typography>
            <Chip
              label={statusLabel}
              color={
                isRunning
                  ? 'warning'
                  : runStatus === 'scheduled'
                    ? 'success'
                    : runStatus === 'disabled'
                      ? 'default'
                      : 'default'
              }
              size="small"
            />
            <Button size="small" variant="outlined" onClick={openCurrentRun}>
              Click view
            </Button>
            <Button size="small" variant="outlined" onClick={openLatestRun}>
              Latest run log
            </Button>
            <Button size="small" variant="contained" onClick={runNow}>
              Run now
            </Button>
            <Button size="small" onClick={load}>
              Refresh
            </Button>
          </Stack>
          {status?.dueReason && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Scheduler: {status.due ? 'due' : 'not due'} — {status.dueReason}
              {status?.config?.lastRunAt ? ` · last run ${status.config.lastRunAt}` : ''}
            </Typography>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            }
            label="Enable scheduled job (required for recurring runs; Run now also turns this on)"
          />

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Frequency</InputLabel>
                <Select
                  label="Frequency"
                  value={form.frequency}
                  onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
                >
                  {FREQUENCIES.map((x) => (
                    <MenuItem key={x.id} value={x.id}>{x.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start date"
                InputLabelProps={{ shrink: true }}
                value={form.startDate || ''}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End date"
                InputLabelProps={{ shrink: true }}
                value={form.endDate || ''}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Messages per account"
                value={form.maxResults}
                onChange={(e) => setForm((f) => ({ ...f, maxResults: Number(e.target.value) || 100 }))}
                inputProps={{ min: 10, max: 500 }}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Provider types (multi-select)
          </Typography>
          <FormGroup row>
            {PROVIDER_OPTIONS.map((p) => (
              <FormControlLabel
                key={p.id}
                control={
                  <Checkbox
                    checked={form.providers.includes(p.id)}
                    onChange={() => toggleProvider(p.id)}
                  />
                }
                label={p.label}
              />
            ))}
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Scope: users / profiles (empty = all admin-enabled accounts)
          </Typography>
          <Alert severity="info" sx={{ mb: 1 }}>
            Load the Email Providers tab once so users and profiles appear here. Leave unchecked to process everyone.
          </Alert>
          <Button size="small" sx={{ mb: 1 }} onClick={loadProviders}>
            Refresh users &amp; profiles
          </Button>
          <Box sx={{ maxHeight: 140, overflow: 'auto', mb: 1, border: '1px solid', borderColor: 'divider', p: 1, borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Users</Typography>
            <FormGroup row>
              {usersFromProviders.map((u) => (
                <FormControlLabel
                  key={u.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.userIds.includes(u.id)}
                      onChange={() => toggleUserScope(u.id)}
                    />
                  }
                  label={`${u.label} (${u.email})`}
                />
              ))}
              {!usersFromProviders.length && (
                <Typography variant="caption">No provider accounts loaded yet.</Typography>
              )}
            </FormGroup>
          </Box>
          <Box sx={{ maxHeight: 180, overflow: 'auto', mb: 2, border: '1px solid', borderColor: 'divider', p: 1, borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Profiles (mailbox accounts)</Typography>
            <FormGroup>
              {providerAccounts.map((a) => (
                <FormControlLabel
                  key={a.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.accountIds.includes(a.id)}
                      onChange={() => toggleAccountScope(a.id)}
                      disabled={a.admin_enabled === false}
                    />
                  }
                  label={`${a.email} · ${a.provider} · ${a.user?.email || a.user_id}${a.admin_enabled === false ? ' (admin disabled)' : ''}`}
                />
              ))}
            </FormGroup>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Exclusion patterns (one per line, e.g. *noreply*)
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={exclusionText}
            onChange={(e) => setExclusionText(e.target.value)}
            placeholder={'*noreply*\n*no-reply*\n*donotreply*'}
            helperText="Matched against the full email address. Also respects Domain Exclusions."
          />

          <Button
            sx={{ mt: 2 }}
            variant="contained"
            onClick={save}
            disabled={saving || form.providers.length === 0}
          >
            {saving ? 'Saving…' : 'Save Job Settings'}
          </Button>
        </Box>
      )}

      {subTab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            All IMAP providers configured by users. Disable a profile to remove it from the scheduled job;
            the owning user cannot edit, delete, or manually extract while disabled.
          </Alert>
          {providersLoading ? (
            <CircularProgress size={28} />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Mailbox</TableCell>
                    <TableCell>Verified</TableCell>
                    <TableCell>Last sync</TableCell>
                    <TableCell align="center">Job enabled</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {providerAccounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No provider accounts configured</TableCell>
                    </TableRow>
                  ) : (
                    providerAccounts.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {[a.user?.first_name, a.user?.last_name].filter(Boolean).join(' ') || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{a.user?.email}</Typography>
                        </TableCell>
                        <TableCell>{a.provider}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{a.email}</TableCell>
                        <TableCell>{a.is_verified ? 'Yes' : 'No'}</TableCell>
                        <TableCell>
                          {a.last_sync_at ? new Date(a.last_sync_at).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={a.admin_enabled !== false}
                            onChange={(e) => setProviderEnabled(a.id, e.target.checked)}
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {subTab === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Promote creates real Company (<code>COM-…</code>) and/or Contact (<code>CON-…</code>) records from
            extracted fields (name, phone, address, website). Prefer Company-Good / Contact-Good tags.
          </Alert>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel>Tag</InputLabel>
                <Select
                  label="Tag"
                  value={promoteTag}
                  onChange={(e) => setPromoteTag(e.target.value)}
                >
                  <MenuItem value="">Select tag</MenuItem>
                  {tags.map((t) => (
                    <MenuItem key={t.code} value={t.code}>
                      {t.label} ({t.category})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Promote mode</InputLabel>
                <Select
                  label="Promote mode"
                  value={promoteMode}
                  onChange={(e) => setPromoteMode(e.target.value)}
                >
                  <MenuItem value="contact">Promote Contact Only</MenuItem>
                  <MenuItem value="company">Promote Company Only</MenuItem>
                  <MenuItem value="both">Promote Both</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                disabled={!promoteTag || promoteLoading}
                onClick={runPromoteByTag}
              >
                {promoteLoading ? 'Promoting…' : `Promote (${promotePreview.length})`}
              </Button>
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Matching unpromoted emails: {promotePreview.length}
            {promotePreview.slice(0, 5).map((e) => (
              <Chip key={e.id} size="small" label={e.email} sx={{ ml: 0.5, mt: 0.5 }} />
            ))}
            {promotePreview.length > 5 ? ' …' : ''}
          </Typography>
        </Box>
      )}

      <Dialog open={Boolean(viewRun)} onClose={() => setViewRun(null)} maxWidth="md" fullWidth>
        <DialogTitle>Job run details</DialogTitle>
        <DialogContent dividers>
          {viewRun && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Status: <strong>{viewRun.status}</strong> · Triggered by: {viewRun.triggered_by} · Frequency:{' '}
                {viewRun.frequency}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Started: {viewRun.started_at || '—'} · Finished: {viewRun.finished_at || '—'}
              </Typography>
              {viewRun.error_message && (
                <Alert severity="error" sx={{ my: 1 }}>{viewRun.error_message}</Alert>
              )}
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Activity log (includes username)</Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: 'grey.100',
                  p: 1.5,
                  borderRadius: 1,
                  maxHeight: 360,
                  overflow: 'auto',
                  fontSize: 12,
                }}
              >
                {(Array.isArray(viewRun.details) ? viewRun.details : [])
                  .map((d) => `${d.at || ''}  ${d.message || ''}`)
                  .join('\n') || 'No details yet.'}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewRun(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
